/**
 * Department visibility rules shared by every screen that filters by department.
 *
 * Two permissions in `HRM_PERMISSIONS.DEPARTMENT` decide what a user may pick and see:
 * - `VIEW_ALL_DEPARTMENTS`: every department.
 * - `VIEW_OWN_DEPARTMENT`: only the departments whose Head of Department role the user holds in the
 *   current organization, plus the department on their own profile. Each department owns one such
 *   role, whose slug is the department's `Key`
 *   (`hrm::department-scope::<organization-slug>::<department-slug>`), so assigning it in the
 *   directory's role dialog is what makes a person the head of that department.
 * - Neither: no department.
 *
 * The data rules (HOB Rule) and the workflows check the same role against a record's DepartmentKey.
 *
 * Kept free of React and network code so it can be unit-tested and reused by any query builder.
 */

/** The middle segment of every department Key, kept apart from the static `hrm::department::*`
 * actions in `src/constant/hr/permissions.ts`. */
export const DEPARTMENT_SCOPE_RESOURCE_GROUP = 'department-scope';

/**
 * Every department's Key looks like `hrm::department-scope::<organization-slug>::<department-slug>`,
 * which is also the slug of that department's Head of Department role — the single source of truth
 * for the shape. IAM roles are project-wide, so the organization segment gives each organization's
 * "Engineering" its own role and tells an admin in IAM which organization it belongs to. Keys created
 * before that (`hrm::department-scope::<department-slug>`) still match.
 */
export const DEPARTMENT_KEY_PATTERN = new RegExp(
  `^hrm::${DEPARTMENT_SCOPE_RESOURCE_GROUP}::(?:[a-z0-9-]+::)?[a-z0-9-]+$`
);

/** Workflows drop department keys longer than 128 characters, so the segments are capped to stay
 * below that (23 + 40 + 2 + 60). */
const ORGANIZATION_SEGMENT_MAX = 40;
const DEPARTMENT_SEGMENT_MAX = 60;

export const toKeySegment = (value: string, maxLength: number) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, maxLength)
    .replace(/^-+|-+$/g, '');

/** The organization's segment of a key: its name slugged, or its id when the name gives none. */
export const organizationKeySegment = (organizationName: string, organizationId = '') =>
  toKeySegment(organizationName, ORGANIZATION_SEGMENT_MAX) ||
  toKeySegment(organizationId, ORGANIZATION_SEGMENT_MAX);

/**
 * Builds a department's Key (and therefore its role slug) from the organization's name
 * and the department's name — this is what the Key field auto-fills with, so admins never type the
 * key or the resource format by hand. An organization name without Latin letters or digits falls
 * back to the organization's id. An empty department name gives an invalid key on purpose, so the
 * form asks for one.
 */
export const buildDepartmentKey = (
  title: string,
  organizationName: string,
  organizationId = ''
) => {
  const organization = organizationKeySegment(organizationName, organizationId);
  const department = toKeySegment(title, DEPARTMENT_SEGMENT_MAX);
  return `hrm::${DEPARTMENT_SCOPE_RESOURCE_GROUP}::${organization}::${department}`;
};

export interface ScopedDepartment {
  ItemId: string;
  Title: string;
  Key?: string;
}

/** Is this role slug a department's Head of Department role? */
export const isDepartmentRole = (slug: string) => DEPARTMENT_KEY_PATTERN.test(slug);

/** The department Keys a user holds as Head of Department roles (from their roles in one organization). */
export const getDepartmentScopeKeys = (roles: readonly string[] | undefined): string[] =>
  (roles ?? []).filter(isDepartmentRole);

/**
 * The departments a user may see. `VIEW_ALL_DEPARTMENTS` gets every department. Otherwise
 * `VIEW_OWN_DEPARTMENT` gets every department whose Head of Department role they hold (several roles
 * give several departments) plus the department on their own profile, so a person is never locked
 * out of their own team even before being made its head. Without either, nothing.
 */
export const resolveVisibleDepartments = ({
  departments,
  canViewAll,
  canViewOwn,
  scopeKeys,
  ownDepartmentId,
}: {
  departments: readonly ScopedDepartment[];
  canViewAll: boolean;
  canViewOwn: boolean;
  scopeKeys: readonly string[];
  ownDepartmentId: string;
}): ScopedDepartment[] => {
  if (canViewAll) return [...departments];
  if (!canViewOwn) return [];
  const keys = new Set(scopeKeys);
  return departments.filter(
    (department) =>
      (department.Key && keys.has(department.Key)) ||
      (ownDepartmentId && department.ItemId === ownDepartmentId)
  );
};

/**
 * The `DepartmentId` clause for a list query: the picked department when the user may see it,
 * otherwise everything they may see (`undefined` = no restriction, for `VIEW_ALL_DEPARTMENTS`).
 * A scoped user with no visible department gets an empty `$in`, which matches nothing.
 */
export const buildDepartmentIdCondition = ({
  selectedId,
  visibleIds,
  canViewAll,
}: {
  selectedId: string;
  visibleIds: readonly string[];
  canViewAll: boolean;
}): string | { $in: string[] } | undefined => {
  if (selectedId && (canViewAll || visibleIds.includes(selectedId))) return selectedId;
  if (canViewAll) return undefined;
  if (visibleIds.length === 1) return visibleIds[0];
  return { $in: [...visibleIds] };
};
